import React, { useState } from 'react';
import { ArrowLeft, TrendingUp, Users, Heart, MessageCircle, Share2, Calendar, BarChart3 } from 'lucide-react';
import { Link } from 'react-router-dom';

const SocialDashboard = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('7d');

  const stats = [
    { label: 'Total Followers', value: '24.5K', change: '+12.5%', icon: <Users size={24} />, color: 'blue' },
    { label: 'Engagement Rate', value: '8.2%', change: '+2.1%', icon: <Heart size={24} />, color: 'red' },
    { label: 'Total Posts', value: '156', change: '+8', icon: <MessageCircle size={24} />, color: 'green' },
    { label: 'Shares', value: '3.2K', change: '+15.3%', icon: <Share2 size={24} />, color: 'purple' },
  ];

  const posts = [
    {
      id: 1,
      content: 'Excited to announce our new product launch! 🚀',
      platform: 'Twitter',
      likes: 234,
      comments: 45,
      shares: 12,
      engagement: 8.5,
      date: '2024-01-15',
      scheduled: false,
    },
    {
      id: 2,
      content: 'Behind the scenes: How we build amazing products',
      platform: 'LinkedIn',
      likes: 567,
      comments: 89,
      shares: 34,
      engagement: 12.3,
      date: '2024-01-14',
      scheduled: false,
    },
    {
      id: 3,
      content: 'New blog post: 10 Tips for Better Productivity',
      platform: 'Facebook',
      likes: 123,
      comments: 23,
      shares: 8,
      engagement: 6.2,
      date: '2024-01-16',
      scheduled: true,
    },
  ];

  const analytics = [
    { label: 'Impressions', value: '125.4K', change: '+18%' },
    { label: 'Reach', value: '89.2K', change: '+15%' },
    { label: 'Clicks', value: '3.4K', change: '+22%' },
    { label: 'Conversions', value: '234', change: '+8%' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link
          to="/portfolio"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft size={20} />
          Back to Portfolio
        </Link>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Social Media Dashboard</h1>
          <p className="text-gray-600">Manage and analyze your social media presence</p>
        </div>

        {/* Period Selector */}
        <div className="flex gap-2 mb-6">
          {['7d', '30d', '90d', 'All'].map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedPeriod === period
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              {period}
            </button>
          ))}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`text-${stat.color}-600`}>{stat.icon}</div>
                <div className="flex items-center gap-1 text-sm text-green-600">
                  <TrendingUp size={16} />
                  {stat.change}
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-1">{stat.label}</p>
              <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Analytics Chart */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 size={24} />
                Analytics Overview
              </h2>
            </div>
            <div className="h-64 bg-gradient-to-br from-primary-100 to-purple-100 rounded-lg flex items-center justify-center mb-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-gray-900 mb-2">125.4K</div>
                <div className="text-gray-600">Total Impressions</div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {analytics.map((metric, index) => (
                <div key={index} className="text-center">
                  <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                  <p className="text-sm text-gray-600 mb-1">{metric.label}</p>
                  <p className="text-sm text-green-600 font-semibold">{metric.change}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Posts */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Calendar size={24} />
              Recent Posts
            </h2>
            <div className="space-y-4">
              {posts.map((post) => (
                <div key={post.id} className="border-b border-gray-200 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-semibold">
                      {post.platform}
                    </span>
                    {post.scheduled && (
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs font-semibold">
                        Scheduled
                      </span>
                    )}
                  </div>
                  <p className="text-gray-700 mb-3 text-sm">{post.content}</p>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Heart size={14} />
                      {post.likes}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle size={14} />
                      {post.comments}
                    </span>
                    <span className="flex items-center gap-1">
                      <Share2 size={14} />
                      {post.shares}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">{post.date}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Posts Table */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Post Performance</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{post.content}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-xs font-semibold">
                        {post.platform}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{post.engagement}%</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{post.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialDashboard;

